import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { openaiTTS, OpenAIVoice, OpenAITTSOptions } from '../lib/openai-tts';
import { supabase } from '../lib/supabase';

export interface VoiceProfile {
  id: string;
  name: string;
  description: string;
  gender: string;
  age: string;
  accent: string;
  tone: string;
  category: string;
}

export type PermissionStatus = 'granted' | 'denied' | 'prompt' | 'checking' | 'unavailable';

export interface PermissionError {
  type: 'microphone' | 'speech-recognition' | 'system' | 'browser';
  message: string;
  code?: string;
  userMessage: string;
  helpUrl?: string;
}

interface VoiceContextType {
  // Audio recording
  isRecording: boolean;
  isPlaying: boolean;
  isSpeaking: boolean;
  currentAudio: HTMLAudioElement | null;

  // Voice recognition
  isListening: boolean;
  transcription: string;
  currentInterimTranscription: string;
  confidence: number;
  speechRecognitionError: string | null;
  clearSpeechRecognitionError: () => void;

  // Permissions
  microphonePermission: PermissionStatus;
  permissionError: PermissionError | null;
  checkMicrophonePermission: () => Promise<PermissionStatus>;
  requestMicrophonePermission: () => Promise<boolean>;
  clearPermissionError: () => void;
  browserSupportsVoice: boolean;
  
  // Recording functions
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  playAudio: (audioBlob: Blob) => Promise<void>;
  stopAudio: () => void;
  
  // Speech synthesis
  speak: (text: string, options?: any) => Promise<void>;
  stopSpeaking: () => void;
  
  // Speech recognition
  startListening: () => Promise<void>;
  stopListening: () => void;
  
  // Voice analysis
  analyzeVoice: (audioBlob: Blob) => Promise<any>;
  
  // Voice profiles
  currentVoiceProfile: VoiceProfile | null;
  availableVoices: VoiceProfile[];
  voiceSettings: {
    voiceId: string;
    stability: number;
    similarityBoost: number;
    style: number;
    speakerBoost: boolean;
  };
  
  // Voice commands
  isVoiceCommandActive: boolean;
  enableVoiceCommands: () => void;
  disableVoiceCommands: () => void;
  
  // Audio content
  audioLibrary: any[];
  generatePersonalizedContent: (type: string, preferences: any) => Promise<void>;
  playAudioContent: (contentId: string) => Promise<void>;
  
  // Storage
  uploadAudioToStorage: (blob: Blob, filename: string) => Promise<string>;
  
  // Voice profile management
  setVoiceProfile: (profileId: string) => void;
  updateVoiceSettings: (settings: any) => void;
  
  // Media recorder
  mediaRecorder: MediaRecorder | null;
}

const VoiceContext = createContext<VoiceContextType | undefined>(undefined);

export const useVoice = () => {
  const context = useContext(VoiceContext);
  if (context === undefined) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }
  return context;
};

interface VoiceProviderProps {
  callObject?: any;
  children: ReactNode;
}

export const VoiceProvider: React.FC<VoiceProviderProps> = ({ callObject, children }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);

  // Speech recognition states
  const [isListening, setIsListening] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [currentInterimTranscription, setCurrentInterimTranscription] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [speechRecognitionError, setSpeechRecognitionError] = useState<string | null>(null);
  const [recognitionInstance, setRecognitionInstance] = useState<SpeechRecognition | null>(null);

  // Permission states
  const [microphonePermission, setMicrophonePermission] = useState<PermissionStatus>('prompt');
  const [permissionError, setPermissionError] = useState<PermissionError | null>(null);
  const [browserSupportsVoice] = useState(() => {
    const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    const hasSpeechRecognition = !!(window.SpeechRecognition || (window as any).webkitSpeechRecognition);
    return hasGetUserMedia && hasSpeechRecognition;
  });
  
  // Voice profiles and settings
  const [currentVoiceProfile, setCurrentVoiceProfileState] = useState<VoiceProfile | null>({
    id: 'nova',
    name: 'Nova',
    description: 'A gentle, caring voice ideal for mental wellness',
    gender: 'female',
    age: 'middle',
    accent: 'american',
    tone: 'gentle',
    category: 'therapeutic'
  });
  
  const [availableVoices, setAvailableVoices] = useState<VoiceProfile[]>([
    {
      id: 'nova',
      name: 'Nova',
      description: 'A gentle, caring voice ideal for mental wellness',
      gender: 'female',
      age: 'middle',
      accent: 'american',
      tone: 'gentle',
      category: 'therapeutic'
    },
    {
      id: 'alloy',
      name: 'Alloy',
      description: 'A balanced, neutral voice suitable for most content',
      gender: 'neutral',
      age: 'middle',
      accent: 'american',
      tone: 'neutral',
      category: 'general'
    },
    {
      id: 'echo',
      name: 'Echo',
      description: 'A clear, articulate voice with good pronunciation',
      gender: 'neutral',
      age: 'middle',
      accent: 'american',
      tone: 'clear',
      category: 'general'
    },
    {
      id: 'fable',
      name: 'Fable',
      description: 'A warm, engaging voice perfect for narratives',
      gender: 'neutral',
      age: 'middle',
      accent: 'american',
      tone: 'warm',
      category: 'storytelling'
    },
    {
      id: 'onyx',
      name: 'Onyx',
      description: 'A deep, authoritative voice for serious content',
      gender: 'male',
      age: 'middle',
      accent: 'american',
      tone: 'confident',
      category: 'professional'
    },
    {
      id: 'shimmer',
      name: 'Shimmer',
      description: 'A bright, energetic voice for positive content',
      gender: 'female',
      age: 'middle',
      accent: 'american',
      tone: 'energetic',
      category: 'uplifting'
    }
  ]);
  
  const [voiceSettings, setVoiceSettings] = useState({
    voiceId: 'nova',
    model: 'tts-1',
    speed: 1.0
  });
  
  // Voice commands
  const [isVoiceCommandActive, setIsVoiceCommandActive] = useState(false);
  
  // Audio library
  const [audioLibrary, setAudioLibrary] = useState<any[]>([]);
  
  // Audio context for Web Audio API
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);

  const checkMicrophonePermission = useCallback(async (): Promise<PermissionStatus> => {
    setMicrophonePermission('checking');

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setMicrophonePermission('unavailable');
        setPermissionError({
          type: 'browser',
          message: 'Media devices not supported',
          userMessage: 'Your browser does not support microphone access. Please use a modern browser like Chrome, Firefox, or Safari.',
          code: 'MEDIA_DEVICES_UNAVAILABLE'
        });
        return 'unavailable';
      }

      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          const status = result.state as PermissionStatus;
          setMicrophonePermission(status);
          return status;
        } catch (e) {
          console.log('Permissions API not fully supported, will check via getUserMedia');
        }
      }

      setMicrophonePermission('prompt');
      return 'prompt';
    } catch (error) {
      console.error('Error checking microphone permission:', error);
      setMicrophonePermission('unavailable');
      return 'unavailable';
    }
  }, []);

  const requestMicrophonePermission = useCallback(async (): Promise<boolean> => {
    setPermissionError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setMicrophonePermission('granted');
      return true;
    } catch (error: any) {
      console.error('Error requesting microphone permission:', error);

      let permError: PermissionError;

      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setMicrophonePermission('denied');
        permError = {
          type: 'microphone',
          message: error.message,
          code: error.name,
          userMessage: 'Microphone access was denied. Please allow microphone access in your browser settings and try again.',
          helpUrl: getBrowserHelpUrl()
        };
      } else if (error.name === 'NotFoundError') {
        setMicrophonePermission('unavailable');
        permError = {
          type: 'system',
          message: error.message,
          code: error.name,
          userMessage: 'No microphone was found on your device. Please connect a microphone and try again.'
        };
      } else if (error.name === 'NotReadableError') {
        setMicrophonePermission('denied');
        permError = {
          type: 'system',
          message: error.message,
          code: error.name,
          userMessage: 'Your microphone is being used by another application. Please close other apps using the microphone and try again.'
        };
      } else {
        setMicrophonePermission('denied');
        permError = {
          type: 'microphone',
          message: error.message || 'Unknown error',
          code: error.name,
          userMessage: 'Unable to access microphone. Please check your browser settings and try again.'
        };
      }

      setPermissionError(permError);
      return false;
    }
  }, []);

  const getBrowserHelpUrl = (): string => {
    const userAgent = navigator.userAgent.toLowerCase();

    if (userAgent.includes('chrome')) {
      return 'https://support.google.com/chrome/answer/2693767';
    } else if (userAgent.includes('firefox')) {
      return 'https://support.mozilla.org/en-US/kb/how-manage-your-camera-and-microphone-permissions';
    } else if (userAgent.includes('safari')) {
      return 'https://support.apple.com/guide/safari/websites-ibrwe2159f50/mac';
    } else if (userAgent.includes('edge')) {
      return 'https://support.microsoft.com/en-us/microsoft-edge/windows-camera-microphone-and-privacy-a83257bc-e990-d54a-d212-b5e41beba857';
    }

    return 'https://support.google.com/chrome/answer/2693767';
  };

  const clearPermissionError = useCallback(() => {
    setPermissionError(null);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const hasPermission = await requestMicrophonePermission();

      if (!hasPermission) {
        throw new Error('Microphone permission denied');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      setMediaRecorder(recorder);
      setIsRecording(true);
      recorder.start();

      setPermissionError(null);
    } catch (error: any) {
      console.error('Error starting recording:', error);

      if (!permissionError) {
        setPermissionError({
          type: 'microphone',
          message: error.message,
          userMessage: 'Unable to start recording. Please ensure microphone access is allowed.',
          code: error.name
        });
      }

      throw error;
    }
  }, [requestMicrophonePermission, permissionError]);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (mediaRecorder && isRecording) {
        mediaRecorder.addEventListener('dataavailable', (event) => {
          if (event.data.size > 0) {
            resolve(event.data);
          } else {
            resolve(null);
          }
        });
        
        mediaRecorder.stop();
        setIsRecording(false);
        
        // Stop all tracks to release the microphone
        mediaRecorder.stream.getTracks().forEach(track => track.stop());
      } else {
        resolve(null);
      }
    });
  }, [mediaRecorder, isRecording]);

  const playAudio = useCallback(async (audioBlob: Blob) => {
    try {
      // Stop any currently playing audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }

      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      setCurrentAudio(audio);
      setIsPlaying(true);

      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      });

      await audio.play();
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsPlaying(false);
    }
  }, [currentAudio]);

  const stopAudio = useCallback(() => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setIsPlaying(false);
    }
  }, [currentAudio]);

  // Speech synthesis
  const speak = useCallback(async (text: string, options?: any) => {
    setIsSpeaking(true);
    
    try {
      const audioElement = await openaiTTS.speakText(text, voiceSettings.voiceId, {
        model: voiceSettings.model as 'tts-1' | 'tts-1-hd',
        speed: voiceSettings.speed,
        ...options
      });
      
      audioElement.onended = () => {
        setIsSpeaking(false);
      };
      
      audioElement.onerror = () => {
        setIsSpeaking(false);
      };
    } catch (error) {
      console.error('OpenAI TTS error:', error);
      setIsSpeaking(false);
    }
  }, []);
  
  const stopSpeaking = useCallback(() => {
    // Stop any currently playing audio
    // Note: OpenAI TTS doesn't have a direct stop method, so we rely on the audio element
    setIsSpeaking(false);
  }, []);
  
  // Speech recognition
  const startListening = useCallback(async () => {
    try {
      const hasPermission = await requestMicrophonePermission();

      if (!hasPermission) {
        setSpeechRecognitionError('Microphone permission is required for speech recognition');
        return Promise.reject(new Error('Microphone permission denied'));
      }

      const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;

      if (!SpeechRecognition) {
        const error = 'Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari.';
        setSpeechRecognitionError(error);
        setPermissionError({
          type: 'browser',
          message: error,
          userMessage: error,
          code: 'SPEECH_RECOGNITION_UNAVAILABLE'
        });
        return Promise.reject(new Error(error));
      }

      setIsListening(true);
      setTranscription('');
      setSpeechRecognitionError(null);
      
      // Create a new recognition instance
      const recognition = new SpeechRecognition();
      
      // Configure recognition
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      
      // Set up event handlers
      recognition.onresult = (event) => {
        let finalTranscript = '';
        let currentInterim = '';
        let maxConfidence = 0;
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          const confidence = event.results[i][0].confidence;
          
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
            // Update max confidence
            if (confidence > maxConfidence) {
              maxConfidence = confidence;
            }
          } else {
            currentInterim += transcript + ' ';
          }
        }
        
        // Only append final transcripts to the cumulative transcription
        if (finalTranscript) {
          setTranscription(prev => prev + finalTranscript);
        }
        
        // Set current interim transcription (will be displayed separately)
        setCurrentInterimTranscription(currentInterim);
        
        // Update confidence if we have a final result
        if (maxConfidence > 0) {
          setConfidence(maxConfidence);
        }
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);

        let errorMessage = `Speech recognition error: ${event.error}`;
        let permError: PermissionError | null = null;

        if (event.error === 'not-allowed' || event.error === 'permission-denied') {
          errorMessage = 'Microphone access was denied. Please allow microphone access in your browser settings.';
          permError = {
            type: 'speech-recognition',
            message: event.error,
            code: event.error,
            userMessage: errorMessage,
            helpUrl: getBrowserHelpUrl()
          };
          setMicrophonePermission('denied');
        } else if (event.error === 'no-speech') {
          errorMessage = 'No speech was detected. Please try speaking again.';
        } else if (event.error === 'audio-capture') {
          errorMessage = 'Microphone could not be accessed. Please check your microphone connection.';
          permError = {
            type: 'system',
            message: event.error,
            code: event.error,
            userMessage: errorMessage
          };
        } else if (event.error === 'network') {
          errorMessage = 'Network error occurred. Please check your internet connection.';
        }

        setSpeechRecognitionError(errorMessage);
        if (permError) {
          setPermissionError(permError);
        }
        setIsListening(false);
      };
      
      recognition.onend = () => {
        // Only set isListening to false if we're not manually stopping
        if (isListening) {
          console.log('Speech recognition ended unexpectedly, restarting...');
          recognition.start();
        }
      };
      
      // Start recognition
      recognition.start();
      setRecognitionInstance(recognition);
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      const errorMessage = `Failed to start speech recognition: ${error instanceof Error ? error.message : 'Unknown error'}`;
      setSpeechRecognitionError(errorMessage);
      setIsListening(false);
      return Promise.reject(error);
    }

    return Promise.resolve();
  }, [requestMicrophonePermission]);
  
  const stopListening = useCallback(() => {
    // Stop the recognition instance if it exists
    if (recognitionInstance) {
      // Clear the interim transcription when stopping
      setCurrentInterimTranscription('');
      recognitionInstance.stop();
      setRecognitionInstance(null);
    }
    setIsListening(false);
  }, []);
  
  const clearSpeechRecognitionError = useCallback(() => {
    setSpeechRecognitionError(null);
  }, []);
  
  // Voice analysis
  const analyzeVoice = useCallback(async (audioBlob: Blob) => {
    console.log('Analyzing voice recording with Supabase Edge Function...');
    
    try {
      // Create FormData to send audio file
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice_recording.webm');
      
      // Get current user ID if available
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        formData.append('userId', session.user.id);
      }
      
      // Call Supabase Edge Function for voice analysis
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-voice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Voice analysis failed: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Voice analysis result:', result);
      
      return result.analysis;
    } catch (error) {
      console.error('Voice analysis failed, using fallback:', error);
      
      // Fallback to mock analysis if API fails
      return {
        emotionalState: 'neutral',
        stressLevel: 50,
        energyLevel: 70,
        speechRate: 150,
        pauseFrequency: 5,
        voiceStability: 80,
        confidence: 0.8
      };
    }
  }, []);
  
  // Voice profile management
  const setVoiceProfile = useCallback((profileId: string) => {
    const profile = availableVoices.find(v => v.id === profileId);
    if (profile) {
      setCurrentVoiceProfileState(profile);
      setVoiceSettings(prev => ({ ...prev, voiceId: profileId }));
    }
  }, [availableVoices]);
  
  const updateVoiceSettings = useCallback((settings: any) => {
    setVoiceSettings(prev => ({ ...prev, ...settings }));
  }, []);
  
  // Voice commands
  const enableVoiceCommands = useCallback(() => {
    setIsVoiceCommandActive(true);
  }, []);
  
  const disableVoiceCommands = useCallback(() => {
    setIsVoiceCommandActive(false);
  }, []);
  
  // Audio content
  const generatePersonalizedContent = useCallback(async (type: string, preferences: any) => {
    // In a real implementation, this would generate personalized audio content
    // For now, we'll add a mock item to the audio library
    const newContent = {
      id: `content-${Date.now()}`,
      title: `${type.charAt(0).toUpperCase() + type.slice(1)} for ${preferences.mood}`,
      type,
      duration: 180, // 3 minutes
      script: 'This is a sample script for personalized audio content.',
      tags: [preferences.mood, preferences.focus, preferences.duration],
      createdAt: new Date()
    };
    
    setAudioLibrary(prev => [newContent, ...prev]);
    return Promise.resolve();
  }, []);
  
  const playAudioContent = useCallback(async (contentId: string) => {
    const content = audioLibrary.find(item => item.id === contentId);
    if (content) {
      // In a real implementation, this would play the audio content
      // For now, we'll just simulate it
      setIsSpeaking(true);
      await new Promise(resolve => setTimeout(resolve, 3000));
      setIsSpeaking(false);
    }
    return Promise.resolve();
  }, [audioLibrary]);
  
  // Storage
  const uploadAudioToStorage = useCallback(async (blob: Blob, filename: string) => {
    console.log('Uploading audio to Supabase Storage...');
    
    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }
      
      // Create a unique filename with user ID and timestamp
      const uniqueFilename = `${session.user.id}/${Date.now()}_${filename}`;
      
      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('audio-recordings')
        .upload(uniqueFilename, blob, {
          contentType: blob.type || 'audio/webm',
          upsert: false
        });
      
      if (error) {
        throw error;
      }
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('audio-recordings')
        .getPublicUrl(uniqueFilename);
      
      console.log('Audio uploaded successfully:', publicUrl);
      return publicUrl;
    } catch (error) {
      console.error('Failed to upload audio to storage:', error);
      
      // Fallback to mock URL if upload fails
      return `https://example.com/audio/${filename}`;
    }
  }, []);

  const value: VoiceContextType = {
    isRecording,
    isPlaying,
    isSpeaking,
    currentAudio,
    startRecording,
    stopRecording,
    playAudio,
    stopAudio,
    mediaRecorder,
    isListening,
    transcription,
    currentInterimTranscription,
    confidence,
    recognitionInstance,
    speechRecognitionError,
    clearSpeechRecognitionError,
    microphonePermission,
    permissionError,
    checkMicrophonePermission,
    requestMicrophonePermission,
    clearPermissionError,
    browserSupportsVoice,
    speak,
    stopSpeaking,
    startListening,
    stopListening,
    analyzeVoice,
    currentVoiceProfile,
    availableVoices,
    voiceSettings,
    isVoiceCommandActive,
    enableVoiceCommands,
    disableVoiceCommands,
    audioLibrary,
    generatePersonalizedContent,
    playAudioContent,
    uploadAudioToStorage,
    setVoiceProfile,
    updateVoiceSettings
  };

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
};